import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { NonFinancialDriverTree, NonFinancialDriverTreeNode } from '../../../types';
import { flattenTree } from '../../../utils/driver-tree';
import { DRIVER_GROUP_FORM_FIELD_IDS } from './constants';

export const useDriversGroupForm = (driverTree?: NonFinancialDriverTree, row?: NonFinancialDriverTreeNode) => {
  const [groupName, setGroupName] = useState(row?.name || '');
  const [selectedDrivers, setSelectedDrivers] = useState<NonFinancialDriverTreeNode[]>(
    row?.children?.filter(child => !child.isGroup) || []
  );
  const [errors, setErrors] = useState<Record<string, { id: string; message: string }>>({});

  const intl = useIntl();
  const hasBeenValidated = useRef(false);

  useEffect(() => {
    setGroupName(row?.name || '');
    setSelectedDrivers(row?.children?.filter(child => !child.isGroup) || []);
    setErrors({});
    hasBeenValidated.current = false;
  }, [row]);

  const availableDrivers = useMemo(() => {
    if (!driverTree) {
      return [];
    }

    const flatDrivers = flattenTree(driverTree.tree);
    return flatDrivers.filter(driver => !driver.isGroup);
  }, [driverTree]);

  const validate = useCallback(() => {
    const newErrors: Record<string, { id: string; message: string }> = {};

    // Group name
    if (!groupName.trim().length) {
      newErrors.name = {
        id: DRIVER_GROUP_FORM_FIELD_IDS.GROUP_NAME,
        message: intl.formatMessage({
          id: 'reporting.validation.group_name_is_required',
          defaultMessage: 'Group name is required'
        })
      };
    }

    if (selectedDrivers.length === 0) {
      newErrors.drivers = {
        id: DRIVER_GROUP_FORM_FIELD_IDS.DRIVERS,
        message: intl.formatMessage({
          id: 'reporting.validation.at_least_one_driver_is_required',
          defaultMessage: 'At least one driver is required'
        })
      };
    }

    setErrors(newErrors);
    hasBeenValidated.current = true;
    return Object.keys(newErrors).length === 0;
  }, [groupName, intl, selectedDrivers.length]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  return {
    groupName,
    setGroupName,
    selectedDrivers,
    setSelectedDrivers,
    availableDrivers,
    errors,
    validate,
    revalidate,
    isEditMode: !!row
  };
};
